// Settings: the Sign-in tab, its pure parts (02-DESIGN/03-console/01-the-console.md
// § Settings: the Sign-in tab; decision 0055): which provider is in use and
// what the card says about it, what each provider asks the workspace to
// create and copy back (02-DESIGN/01-identity/03-signing-in.md § what a tenant
// admin sets up on their provider), and who a switch would carry over — the
// service's rule (01-tenants-and-roles.md § mapping rows, *a switch*),
// mirrored for the switch's form, which says it before anything happens. The
// switch's own answer is the authority; this is the preview.

import type { ProviderConfig, Row } from "../../wire/auth.ts";
import { ProviderID } from "../../wire/auth.ts";
import { providerDraftOf, type ProviderDraft } from "./provider.ts";

/** Kind is the provider an admin picks in the set-up's first step. */
export type Kind = "entra" | "okta" | "google" | "slack" | "other";

/** Preset is what one kind of provider asks for, in that provider's words. */
export interface Preset {
  kind: Kind;
  /** The display name people see, by default. */
  name: string;
  /** What to create at the provider. */
  creates: string;
  /** What the provider calls the field the redirect address goes in. */
  redirectField: string;
  /** What it calls the field the sign-out address goes in; absent where it has no end-session. */
  logoutField?: string;
  /** Where the provider wants one client for the browser and another for the terminal. */
  twoClients: boolean;
  /** Where the browser's code is exchanged with a secret Impire holds. */
  secret: boolean;
  /** The issuer, where it is the same for everyone; a placeholder otherwise. */
  issuer: string;
  issuerFixed: boolean;
  /** The claims filled in for it. */
  draft: Partial<ProviderDraft>;
  /** The claim an admin rule keys on by default, and an example value. */
  ruleClaim: string;
  ruleExample: string;
}

export const PRESETS: readonly Preset[] = [
  {
    kind: "entra",
    name: "Microsoft Entra",
    creates:
      "One app registration, with a single-page application platform for the console. Allow public client flows, for the terminal. Define app roles and assign them to people or groups.",
    redirectField: "Single-page application redirect URI",
    logoutField: "Redirect URI (for sign-out)",
    twoClients: false,
    secret: false,
    issuer: "https://login.microsoftonline.com/<directory id>/v2.0",
    issuerFixed: false,
    draft: { roleClaim: "roles", orgClaim: "tid", subjectClaim: "oid", handleClaim: "email" },
    ruleClaim: "roles",
    ruleExample: "Impire.Admin",
  },
  {
    kind: "okta",
    name: "Okta",
    creates:
      "A single-page application for the console and a native application with the device grant for the terminal. Enable the grant in the authorization server's policy, and add a groups claim under a filter.",
    redirectField: "Sign-in redirect URI",
    logoutField: "Sign-out redirect URI",
    twoClients: true,
    secret: false,
    issuer: "https://<your org>.okta.com",
    issuerFixed: false,
    draft: { roleClaim: "groups", handleClaim: "email" },
    ruleClaim: "groups",
    ruleExample: "Impire Admins",
  },
  {
    kind: "google",
    name: "Google",
    creates:
      "A web-application client for the console, with its secret, and a desktop-application client for the terminal.",
    redirectField: "Authorized redirect URI",
    twoClients: true,
    secret: true,
    issuer: "https://accounts.google.com",
    issuerFixed: true,
    draft: { orgClaim: "hd", handleClaim: "email" },
    ruleClaim: "email",
    ruleExample: "alice@acme.example",
  },
  {
    kind: "slack",
    name: "Slack",
    creates: "A Slack app with Sign in with Slack, and its client secret.",
    redirectField: "Redirect URL",
    twoClients: false,
    secret: true,
    issuer: "https://slack.com",
    issuerFixed: true,
    draft: { orgClaim: "https://slack.com/team_id", handleClaim: "email" },
    ruleClaim: "email",
    ruleExample: "alice@acme.example",
  },
  {
    kind: "other",
    name: "",
    creates:
      "One client for the console and, where the provider wants a separate one, one for the terminal. Note its issuer exactly as the provider states it.",
    redirectField: "Redirect URI",
    logoutField: "Post-logout redirect URI",
    twoClients: false,
    secret: false,
    issuer: "https://",
    issuerFixed: false,
    draft: { handleClaim: "email" },
    ruleClaim: "email",
    ruleExample: "alice@acme.example",
  },
];

/** presetOf is a kind's preset; an unknown kind is Another OpenID Connect provider. */
export function presetOf(kind: Kind): Preset {
  return PRESETS.find((p) => p.kind === kind) ?? (PRESETS[PRESETS.length - 1] as Preset);
}

/** KIND_LABELS are the first step's choices. */
export const KIND_LABELS: Readonly<Record<Kind, string>> = {
  entra: "Microsoft Entra",
  okta: "Okta",
  google: "Google",
  slack: "Slack",
  other: "Another OpenID Connect provider",
};

/** kindOf guesses the kind a saved configuration is, from its issuer. */
export function kindOf(c: Pick<ProviderConfig, "issuer"> | undefined): Kind {
  const iss = c?.issuer ?? "";
  if (iss.startsWith("https://login.microsoftonline.com/")) {
    return "entra";
  }
  if (iss === "https://accounts.google.com") {
    return "google";
  }
  if (iss === "https://slack.com") {
    return "slack";
  }
  if (/\.okta(preview)?\.com/.test(iss)) {
    return "okta";
  }
  return "other";
}

/** InUse is which provider the workspace signs in through, as the card shows it. */
export type InUse = "platform" | "own" | "managed";

/** inUseOf reads the provider read's answer; an answer without it is the platform's. */
export function inUseOf(id: string | undefined): InUse {
  return id === ProviderID.own ? "own" : id === ProviderID.managed ? "managed" : "platform";
}

/** Carried is one person a switch carries over: their email and their role. */
export interface Carried {
  email: string;
  role: string;
}

/** Carry is what a switch would carry over, and the rules and people it would not. */
export interface Carry {
  carried: Carried[];
  notCarried: Row[];
}

/** SUBJECT_CLAIMS are the stable-subject claims a row on one person keys on. */
const SUBJECT_CLAIMS = new Set(["sub", "oid", "id"]);

/**
 * carryOf is the service's rule for a switch away from `from`: each row
 * under it keyed on a person — their subject, or a waiting row on the handle
 * — whose email is known becomes a waiting row on that email under the
 * provider switched to, the highest role kept where a person has several;
 * every other row under it is not carried. A row the blueprint requires is
 * the parent's and stays where it is.
 */
export function carryOf(rows: readonly Row[], from: string, subjectClaim?: string): Carry {
  const carried: Carried[] = [];
  const notCarried: Row[] = [];
  const at = new Map<string, number>();
  for (const r of rows) {
    if (r.provider !== from || r.required === true) {
      continue;
    }
    const person =
      r.pending === true ||
      SUBJECT_CLAIMS.has(r.claim) ||
      (subjectClaim !== undefined && r.claim === subjectClaim);
    const email = !person ? "" : r.pending === true ? r.value : (r.display ?? "");
    if (!email.includes("@")) {
      notCarried.push(r);
      continue;
    }
    const key = email.toLowerCase();
    const i = at.get(key);
    if (i !== undefined) {
      const c = carried[i] as Carried;
      if (r.role === "admin") {
        c.role = "admin";
      }
      continue;
    }
    at.set(key, carried.length);
    carried.push({ email, role: r.role });
  }
  return { carried, notCarried };
}

/** whoOf names a row that was not carried, for the admin who must add it again. */
export function whoOf(r: Row): string {
  if (r.display !== undefined && r.display !== "") {
    return r.display;
  }
  return `${r.claim} = ${r.value}`;
}

/** isRule reports whether a row is an access rule on a claim, rather than one person. */
export function isRule(r: Row): boolean {
  return r.pending !== true && !SUBJECT_CLAIMS.has(r.claim);
}

/**
 * refusalOf words a refused switch in one sentence (§ Settings: the Sign-in
 * tab): the proof that matched no admin rule, or the service's own words.
 */
export function refusalOf(err: unknown, provider: string): string {
  const message =
    typeof err === "object" && err !== null && "message" in err ? String(err.message) : String(err);
  if (/no admin's|not through the provider|did not verify/.test(message)) {
    return `Your sign-in matched no admin rule under ${provider}; add one and try again. Nothing changed.`;
  }
  return `The switch did not happen: ${message}. Nothing changed.`;
}

/** SetUpDraft is the set-up's state across its steps. */
export interface SetUpDraft extends ProviderDraft {
  kind: Kind;
  displayName: string;
  secret: string;
  ruleClaim: string;
  ruleValue: string;
}

/** draftFor is a set-up's first draft: a provider kind's preset, or a saved configuration filled in. */
export function draftFor(kind: Kind, base?: ProviderConfig): SetUpDraft {
  const preset = presetOf(kind);
  const fields =
    base !== undefined ? providerDraftOf(base) : { ...providerDraftOf(undefined), ...preset.draft };
  if (base === undefined && preset.issuerFixed) {
    fields.issuer = preset.issuer;
  }
  return {
    ...fields,
    kind,
    displayName: base?.display_name ?? preset.name,
    secret: "",
    ruleClaim: preset.ruleClaim,
    ruleValue: "",
  };
}

/** clientsOf is which client ids the set-up asks a provider kind for (ProviderFields' `clients`). */
export function clientsOf(kind: Kind): "one" | "two" | "either" {
  return presetOf(kind).twoClients ? "two" : kind === "other" ? "either" : "one";
}

/** Step is one of the set-up's four steps. */
export type Step = 1 | 2 | 3 | 4;

const KINDS: readonly Kind[] = ["entra", "okta", "google", "slack", "other"];

/**
 * setUpName is the set-up's form name in the history entry once it is under
 * way — `sign-in/set-up:entra:3`, or `sign-in/change:okta:3` — so a refresh
 * returns to the same step for the same provider.
 */
export function setUpName(changing: boolean, kind: Kind, step: Step): string {
  return `sign-in/${changing ? "change" : "set-up"}:${kind}:${String(step)}`;
}

/** setUpStepOf reads the provider and the step back from a set-up's form name; undefined for a set-up just opened. */
export function setUpStepOf(form: string | undefined): { kind: Kind; step: Step } | undefined {
  const m = /^sign-in\/(?:set-up|change):([a-z]+):([1-4])$/.exec(form ?? "");
  const kind = m?.[1] as Kind | undefined;
  if (m === null || kind === undefined || !KINDS.includes(kind)) {
    return undefined;
  }
  return { kind, step: Number(m[2]) as Step };
}

/** DRAFT is the field of the history entry's state that keeps the set-up's values, never its secret. */
const DRAFT = "setUpDraft";

/** withDraft is the history entry's state with the set-up's values kept — every one but the client secret. */
export function withDraft(state: Record<string, unknown>, d: SetUpDraft): Record<string, unknown> {
  const kept: Partial<SetUpDraft> = { ...d };
  delete kept.secret;
  return { ...state, [DRAFT]: kept };
}

/**
 * keptDraft is the set-up's values a history entry kept, over `base`: each
 * field the base has, of the same type, and the client secret always empty.
 */
export function keptDraft(state: unknown, base: SetUpDraft): SetUpDraft {
  const kept =
    typeof state === "object" && state !== null ? (state as Record<string, unknown>)[DRAFT] : undefined;
  if (typeof kept !== "object" || kept === null) {
    return base;
  }
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(kept as Record<string, unknown>)) {
    if (
      k !== "secret" &&
      k !== "kind" &&
      k in base &&
      typeof v === typeof (base as unknown as Record<string, unknown>)[k]
    ) {
      out[k] = v;
    }
  }
  return out as unknown as SetUpDraft;
}

/**
 * setUpOf is which set-up form the Sign-in tab draws for the form named in
 * the history entry: a new set-up only while the workspace has no provider of
 * its own — once one is saved, a set-up reopened by a refresh or Forward is
 * its change, filled in, and never a blank first step that could overwrite
 * it — and a change only where there is one to change.
 */
export function setUpOf(open: string | undefined, configured: boolean): "new" | "change" | undefined {
  const base = (open ?? "").split(":")[0];
  if (base === "sign-in/set-up") {
    return configured ? "change" : "new";
  }
  if (base === "sign-in/change" && configured) {
    return "change";
  }
  return undefined;
}
